import { formatINR } from "@templeos/shared";
import { Button, EmptyState, StatCard, useConfirm, useToast } from "@templeos/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { BillPanel } from "../components/BillPanel.js";
import { useBill } from "../components/checkout.js";
import { Shell } from "../components/Shell.js";
import { CashAlert, ShiftDrawer, useShiftTally } from "../components/ShiftPanel.js";
import { ReprintModal } from "../components/SupervisorActions.js";
import { api, NetworkError } from "../lib/api.js";
import { addItem, CartError, type Cart } from "../lib/cart.js";
import { db } from "../lib/db.js";
import { openCustomerDisplay } from "../lib/display.js";
import { pendingByItem } from "../lib/queue.js";
import { quotaBreach, stockLeft } from "../lib/rules.js";
import { SELL_KEYS, type SellAction } from "../lib/shortcuts.js";
import type { PosItem, QueuedSale } from "../lib/types.js";
import { useSession } from "../session.js";
import { ItemTile } from "./Sell.js";

const QUICK = [1, 2, 5, 10] as const;
const STOCK_REFRESH_MS = 30_000;

/** GET /api/stores/prasad-stock (CONTRACTS.md, T3). Cached for offline use. */
function usePrasadStock(refreshKey: unknown): { stock: Map<string, number> | null; error: string | null } {
  const [stock, setStock] = useState<Map<string, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const rows = await api.get<{ catalogItemId: string; qtyAvailable: number }[]>("/api/stores/prasad-stock");
        await db.setMeta("prasadStock", rows);
        if (alive) { setStock(new Map(rows.map((r) => [r.catalogItemId, Number(r.qtyAvailable)]))); setError(null); }
      } catch (e) {
        const cached = await db.getMeta<{ catalogItemId: string; qtyAvailable: number }[]>("prasadStock");
        if (!alive) return;
        if (cached) setStock(new Map(cached.map((r) => [r.catalogItemId, Number(r.qtyAvailable)])));
        setError(e instanceof NetworkError ? "Offline: stock shown as last known." : "Live stock unavailable; the server still refuses out-of-stock sales.");
      }
    };
    void load();
    const iv = setInterval(load, STOCK_REFRESH_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [refreshKey]);
  return { stock, error };
}

/** SOW Module 36: prasad touch terminal with stock on every tile, quick-add buttons, UPI QR and cash drawer kick. */
export function Prasad() {
  const s = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { sales, t, drawer, quotaUsed } = useShiftTally();
  const items = useMemo(() => (s.config?.items ?? []).filter((i) => i.kind === "prasad"), [s.config]);
  const [refresh, setRefresh] = useState(0);
  // Fresh stock after every bill and every completed upload (a synced sale is in the server's figure from then on).
  const { stock, error: stockError } = usePrasadStock(`${refresh}:${s.sync.lastSyncAt ?? ""}`);
  // Bills still on this terminal are not in the server's stock yet: without this, an offline counter sells past zero.
  const pending = useMemo(() => pendingByItem(sales), [sales]);
  const inCart = (cart: Cart, id: string) => cart.filter((l) => l.itemId === id).reduce((n, l) => n + l.qty, 0);
  const left = (id: string, cart: Cart) => stockLeft(stock, id, inCart(cart, id), pending.get(id) ?? 0);
  const cashierQuota = s.config?.cashierQuota ?? null;
  const validate = useCallback((cart: Cart) => {
    for (const i of items) {
      const want = inCart(cart, i.id);
      const have = stockLeft(stock, i.id, 0, pending.get(i.id) ?? 0);
      if (want && have !== null && want > have) return `Only ${have} ${i.name} left at this counter.`;
    }
    return quotaBreach(cart, items, pending, cashierQuota, quotaUsed);
  }, [items, stock, pending, cashierQuota, quotaUsed]);
  const prasadItems = s.config ? items : null;
  const bill = useBill(`tos-pos-cart:${s.me?.counter?.code ?? ""}:${s.me?.username ?? ""}:prasad`, prasadItems, validate);
  const [selected, setSelected] = useState<string | null>(null);
  const [scan, setScan] = useState("");
  const [reprint, setReprint] = useState<{ receiptId: string; local?: QueuedSale } | null>(null);
  const [shiftOpen, setShiftOpen] = useState(false);
  const scanRef = useRef<HTMLInputElement>(null);
  const lastRef = bill.lastSale?.clientRef;
  useEffect(() => { if (lastRef) setRefresh((n) => n + 1); }, [lastRef]);

  const add = (item: PosItem, qty: number) => {
    const room = left(item.id, bill.cart);
    if (room !== null && qty > room) { toast(room ? `Only ${room} more ${item.name} in stock.` : `${item.name} is out of stock.`, "danger"); return; }
    try {
      bill.setCart(addItem(bill.cart, item, qty));
      setSelected(item.id);
    } catch (e) {
      if (e instanceof CartError) toast(e.message, "danger"); else throw e;
    }
  };
  const sel = items.find((i) => i.id === selected) ?? null;

  const onScan = () => {
    const code = scan.trim().toUpperCase();
    const hit = items.find((i) => i.code.toUpperCase() === code);
    if (hit) add(hit, 1); else if (code) toast(`No prasad item with code ${code}.`, "danger");
    setScan("");
  };

  const lastSynced = [...sales].reverse().find((x) => x.receiptId && !x.voided);
  const onKey = async (a: SellAction) => {
    switch (a) {
      case "search": scanRef.current?.focus(); break;
      case "payCash": bill.pay.setChoice("cash"); break;
      case "payUpi": bill.pay.setChoice("upi"); break;
      case "paySplit": bill.pay.setChoice("split"); break;
      case "checkout": void bill.submit(); break;
      case "clearCart":
        if (bill.cart.length && (await confirm({ title: "Clear this bill?", confirmLabel: "Clear bill", danger: true }))) bill.setCart([]);
        break;
      case "reprintLast":
        if (lastSynced?.receiptId) setReprint({ receiptId: lastSynced.receiptId, local: lastSynced });
        else toast("The last bill has not reached the server yet; reprint after it syncs.", "danger");
        break;
      case "history": navigate("/history"); break;
      case "shiftSummary": setShiftOpen(true); break;
      case "customerDisplay": openCustomerDisplay(); break;
      case "lock": s.lock(); break;
      case "fastMode": break;
    }
  };

  return (
    <Shell keys={SELL_KEYS} onKey={(a) => void onKey(a)} disabledKeys={["fastMode"]}>
      <div className="pos-sell">
        <section className="pos-left" aria-label="Prasad items">
          <div className="pos-tally">
            <StatCard label="Bills this shift" value={t.bills} />
            <StatCard label="Items in bill" value={bill.cart.reduce((n, l) => n + l.qty, 0)} tone="accent" />
            <StatCard label="Collected" value={formatINR(t.amount, { decimals: false })} tone="success" />
            <StatCard label="Waiting to sync" value={t.pending} tone={t.pending ? "danger" : undefined} />
          </div>
          <CashAlert drawer={drawer} />
          {stockError && <div className="tos-alert tone-warning" role="status">{stockError}</div>}
          <div className="tos-row" style={{ alignItems: "flex-end" }}>
            <div className="tos-field" style={{ flex: 1, minWidth: 240 }}>
              <label className="tos-label" htmlFor="pos-prasad-scan">Scan packet barcode (F1)</label>
              <input id="pos-prasad-scan" ref={scanRef} className="tos-input" autoComplete="off" value={scan} placeholder="Scan or type item code"
                onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onScan(); } }} />
            </div>
            <Button onClick={() => setRefresh((n) => n + 1)}>Refresh stock</Button>
          </div>
          <div className="pos-quick" role="group" aria-label={sel ? `Quick add ${sel.name}` : "Quick add (select an item first)"}>
            {QUICK.map((n) => (
              <Button key={n} variant="secondary" disabled={!sel} onClick={() => { if (sel) add(sel, n); }}>+{n}</Button>
            ))}
          </div>
          {!items.length ? (
            <EmptyState title="No prasad items at this counter" action={<Button onClick={() => void s.reloadConfig()}>Reload counter items</Button>}>
              Sign in at the prasad counter (C5) to sell prasad, or reload if the items were just added.
            </EmptyState>
          ) : (
            <div className="pos-tiles">
              {items.map((i) => {
                const room = left(i.id, bill.cart);
                return <ItemTile key={i.id} item={i} pressed={selected === i.id} onAdd={() => add(i, 1)}
                  disabledReason={room === 0 ? (stock?.get(i.id) ? "No more in stock" : "Out of stock") : null}
                  badge={room === null ? "Stock —" : `Stock ${room}`} />;
              })}
            </div>
          )}
        </section>
        <BillPanel bill={bill} askPayer={false} roomFor={(l) => (l.itemId ? left(l.itemId, bill.cart) : null)} />
      </div>
      <ReprintModal target={reprint} onClose={() => setReprint(null)} />
      <ShiftDrawer open={shiftOpen} onClose={() => setShiftOpen(false)} />
    </Shell>
  );
}
